/**
 * BatchPage — one batch (TRA-863): its status and counts, the paths and
 * options it ran with, and its files one page at a time. This is the page
 * a customer opens on completed_with_errors: filter the files to Failed and
 * each row says what went wrong with that document.
 */

import { ArrowLeft } from 'lucide-react'
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import { getApiErrorDetail, getApiErrorStatus } from '@/api/client'
import type { BatchItemResponse, BatchResponse } from '@/api/generated/model'
import { SectionHeading } from '@/components/models/home/ModelHomeSection'
import { BatchItemsTable } from '@/components/pipelines/BatchItemsTable'
import { BatchStatusBadge } from '@/components/pipelines/BatchStatusBadge'
import { Pager } from '@/components/pipelines/Pager'
import { pipelinePath } from '@/components/pipelines/paths'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { Skeleton } from '@/components/ui/skeleton'
import { useTeam } from '@/contexts/TeamContext'
import {
  isBatchActive,
  useBatch,
  useBatchItems,
  useCancelBatch,
  usePipelines,
} from '@/hooks/usePipelines'

const PAGE_SIZE = 100

type ItemFilter = BatchItemResponse['status'] | 'all'
const ITEM_FILTERS: { value: ItemFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'failed', label: 'Failed' },
  { value: 'done', label: 'Done' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'running', label: 'Running' },
  { value: 'pending', label: 'Pending' },
]

function isItemFilter(value: string | null): value is ItemFilter {
  return ITEM_FILTERS.some((filter) => filter.value === value)
}

function formatDateTime(iso: string | null): string {
  if (iso === null) return '—'
  return new Date(iso).toLocaleString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * The batch's counts in the dashboard's stat-strip dialect: hairlines above
 * and below, one cell per count. Failed is the one number that changes what
 * the reader does next, so it alone carries a tint when it is not zero.
 */
function CountStrip({ batch }: { batch: BatchResponse }): React.JSX.Element {
  const counts: [string, number, boolean][] = [
    ['Files', batch.item_total, false],
    ['Done', batch.item_done, false],
    ['Skipped', batch.item_skipped, false],
    ['Failed', batch.item_failed, batch.item_failed > 0],
  ]
  return (
    <div className="grid grid-cols-2 border-y border-border py-3 sm:grid-cols-4">
      {counts.map(([label, value, alert], index) => (
        <div
          key={label}
          className={`px-4 py-1 ${index > 0 ? 'sm:border-l sm:border-border' : 'sm:pl-0'}`}
        >
          <p className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">
            {label}
          </p>
          <p
            className={`mt-0.5 font-mono text-sm tabular-nums ${alert ? 'text-destructive' : ''}`}
          >
            {value.toLocaleString()}
          </p>
        </div>
      ))}
    </div>
  )
}

function BatchDetails({
  batch,
  pipelineLabel,
}: {
  batch: BatchResponse
  pipelineLabel: string
}): React.JSX.Element {
  const rows: [string, React.ReactNode][] = [
    [
      'Pipeline',
      <Link key="pipeline" to={pipelinePath(batch.pipeline)} className="hover:underline">
        {pipelineLabel}
      </Link>,
    ],
    [
      'Input path',
      <span key="input" className="break-all font-mono text-xs">
        {batch.input_path}
      </span>,
    ],
    [
      'Output path',
      <span key="output" className="break-all font-mono text-xs">
        {batch.output_path}
      </span>,
    ],
    ['Created', formatDateTime(batch.created_at)],
    ['Started', formatDateTime(batch.started_at)],
    ['Finished', formatDateTime(batch.finished_at)],
  ]
  const options = Object.entries(batch.options).map(
    ([key, value]) => `${key}: ${typeof value === 'string' ? value : JSON.stringify(value)}`,
  )
  return (
    <div className="space-y-5">
      <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-3">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="pt-0.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              {label}
            </dt>
            <dd className="m-0 text-sm">{value}</dd>
          </div>
        ))}
      </dl>
      {options.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {options.map((chip) => (
            <Badge key={chip} variant="outline" className="normal-case">
              {chip}
            </Badge>
          ))}
        </div>
      )}
      {batch.error !== '' && (
        <p role="alert" className="border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {batch.error}
        </p>
      )}
    </div>
  )
}

export default function BatchPage(): React.JSX.Element {
  const { name = '', batchId = '' } = useParams<{ name: string; batchId: string }>()
  const { isRestricted } = useTeam()
  const [searchParams, setSearchParams] = useSearchParams()
  const filterParam = searchParams.get('status')
  const filter: ItemFilter = isItemFilter(filterParam) ? filterParam : 'all'
  const offset = Math.max(0, Number(searchParams.get('offset') ?? '0') || 0)

  const batch = useBatch(batchId)
  const pipelines = usePipelines()
  const active = batch.data !== undefined && isBatchActive(batch.data)
  const items = useBatchItems(
    batchId,
    { status: filter === 'all' ? undefined : filter, offset, limit: PAGE_SIZE },
    { active },
  )
  const cancel = useCancelBatch()

  if (batch.isLoading) {
    return (
      <PageContainer className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
      </PageContainer>
    )
  }
  if (batch.isError && getApiErrorStatus(batch.error) === 404) {
    return <Navigate to={pipelinePath(name)} replace />
  }
  if (batch.isError || batch.data === undefined) {
    return (
      <PageContainer>
        <p className="text-sm text-destructive">Failed to load batch.</p>
      </PageContainer>
    )
  }

  const row = batch.data
  const pipelineLabel =
    pipelines.data?.pipelines.find((p) => p.name === row.pipeline)?.display_name ?? row.pipeline
  const itemRows = items.data?.items ?? []
  const total = items.data?.total ?? 0

  function setFilter(next: ItemFilter): void {
    const params = new URLSearchParams(searchParams)
    if (next === 'all') params.delete('status')
    else params.set('status', next)
    params.delete('offset')
    setSearchParams(params, { replace: true })
  }

  function setOffset(next: number): void {
    const params = new URLSearchParams(searchParams)
    if (next <= 0) params.delete('offset')
    else params.set('offset', String(next))
    setSearchParams(params, { replace: true })
  }

  function handleCancel(): void {
    cancel.mutate(
      { batchId: row.id },
      {
        onSuccess: () => {
          toast.success('Batch cancelled. Files already in flight will finish.')
        },
        onError: (err) => {
          toast.error(getApiErrorDetail(err) ?? 'The batch was not cancelled')
        },
      },
    )
  }

  return (
    <PageContainer className="space-y-8">
      <Link
        to={pipelinePath(row.pipeline)}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        {pipelineLabel}
      </Link>

      <PageHeader
        title="Batch"
        badge={<BatchStatusBadge status={row.status} />}
        description={row.id}
        actions={
          !isRestricted && active ? (
            <Button variant="outline" onClick={handleCancel} disabled={cancel.isPending}>
              {cancel.isPending ? 'Cancelling…' : 'Cancel batch'}
            </Button>
          ) : undefined
        }
      />

      <CountStrip batch={row} />
      <BatchDetails batch={row} pipelineLabel={pipelineLabel} />

      <section className="space-y-3">
        <SectionHeading
          title="Files"
          subtitle="Each file under the input path and how far it got."
          action={
            <div className="flex flex-wrap gap-1" role="group" aria-label="Filter files by status">
              {ITEM_FILTERS.map((option) => (
                <Button
                  key={option.value}
                  size="sm"
                  variant={filter === option.value ? 'default' : 'outline'}
                  aria-pressed={filter === option.value}
                  onClick={() => {
                    setFilter(option.value)
                  }}
                >
                  {option.label}
                </Button>
              ))}
            </div>
          }
        />

        {items.isLoading && (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        )}

        {items.isError && (
          <p className="text-sm text-destructive">
            Failed to load files: {getApiErrorDetail(items.error) ?? 'unknown error'}
          </p>
        )}

        {!items.isLoading && !items.isError && (
          <>
            {itemRows.length === 0 ? (
              <EmptyState
                title={filter === 'all' ? 'No files listed yet' : 'No files with this status'}
                description={
                  filter === 'all'
                    ? 'Files appear once the worker has listed the input path.'
                    : undefined
                }
              />
            ) : (
              <div className="rounded-md border border-border">
                <BatchItemsTable items={itemRows} />
              </div>
            )}
            {/* Always offered when there is somewhere to go: polling can empty
                a filtered page under the reader, and the empty state alone
                would strand them past the end. */}
            <Pager
              offset={offset}
              count={itemRows.length}
              total={total}
              pageSize={PAGE_SIZE}
              onOffsetChange={setOffset}
            />
          </>
        )}
      </section>
    </PageContainer>
  )
}
